import dayjs from "dayjs";
import { IsoDateString, IsoDateTimeString, IsoTimeString } from "src/types/dateTime.ts";
import { dayJsToDateIsoString, formatDayJs } from "src/utils/dayjsUtils.ts";

/**
 * Stored activity due. All-day activities store the start of their due date in the time zone where they were set.
 */
export interface SalesLeadActivityDue {
	dueAt: IsoDateTimeString;
	allDay: boolean;
}

/**
 * Activity due as a date and an optional time in the browser's time zone. No time means an all-day activity.
 */
export interface SalesLeadActivityLocalDue {
	dueDate: IsoDateString;
	dueTime: IsoTimeString | null;
}

export function toSalesLeadActivityDue({ dueDate, dueTime }: SalesLeadActivityLocalDue): SalesLeadActivityDue {
	const dueAt = dueTime == null ? dayjs(dueDate) : dayjs(`${dueDate}T${dueTime}`);
	return {
		dueAt: dueAt.toISOString(),
		allDay: dueTime == null,
	};
}

/**
 * Returns the current stored due when the local due is unchanged. Re-deriving it would move the due of an all-day
 * activity to the start of the day in the editor's time zone even when only other fields were edited.
 */
export function resolveSalesLeadActivityDue(
	localDue: SalesLeadActivityLocalDue,
	currentDue: SalesLeadActivityDue | null,
): SalesLeadActivityDue {
	if (currentDue != null) {
		const currentLocalDue = toSalesLeadActivityLocalDue(currentDue);
		if (currentLocalDue.dueDate === localDue.dueDate && currentLocalDue.dueTime === localDue.dueTime) {
			return currentDue;
		}
	}
	return toSalesLeadActivityDue(localDue);
}

export function toSalesLeadActivityLocalDue({ dueAt, allDay }: SalesLeadActivityDue): SalesLeadActivityLocalDue {
	const localDueAt = dayjs(dueAt);
	return {
		dueDate: dayJsToDateIsoString(localDueAt),
		dueTime: allDay ? null : localDueAt.format("HH:mm:ss"),
	};
}

/**
 * Formats an activity due date, with the due time when the activity is not an all-day activity.
 */
export function formatSalesLeadActivityDue({ dueAt, allDay }: SalesLeadActivityDue): string {
	const localDueAt = dayjs(dueAt);
	const formattedDate = formatDayJs(localDueAt, "L");
	if (allDay) return formattedDate;
	return `${formattedDate} ${localDueAt.format("H:mm")}`;
}
